// Fork from https://github.com/bluzky/nice-select2
import { triggerEvent as trigger, getAttribute as attr, hasClass } from '../utils';

var defaultOptions = {
    data: null,
    searchable: false,
    placeholder: "Select an option",
    searchPlaceholder: "Search",
    selectedText: "selected",
    showSelectees: false,
    autoWidth: true
};

export default function DropdownList(element, options) {
    // console.log('DropdownList', element, options);
    this.el = element;

    var dataset = {
        searchable: this.el.dataset.searchable === "true",
        placeholder: this.el.dataset.placeholder || defaultOptions.placeholder,
        searchPlaceholder: this.el.dataset.searchPlaceholder || defaultOptions.searchPlaceholder,
        selectedText: this.el.dataset.selectedText || defaultOptions.selectedText,
        showSelectees: this.el.dataset.showSelectees === "true",
        autoWidth: this.el.dataset.autoWidth === "true"
    }
    this.config = Object.assign({}, defaultOptions, dataset, options || {});
    this.data = this.config.data;
    this.selectees = [];

    this.dropdown = null;
    this.multiple = attr(this.el, "multiple");
    this.disabled = attr(this.el, "disabled");
    this.parents = this._getScrollParents();
    this.overflow = this.el.dataset.floating !== "false";

    this.create();
};

DropdownList.prototype.create = function () {
    this.el.classList.add("dropdownlist-select");
    if (this.data) {
        this.process(this.data);
    } else {
        this.extract();
    }

    this.render();
    this.bind();
};

DropdownList.prototype.process = function (data) {
    var options = [];
    data.forEach(datum => {
        options.push({
            data: datum,
            attributes: {
                selected: !!datum.selected,
                disabled: !!datum.disabled,
                optgroup: !!datum.optgroup
            }
        });
    });
    this.options = options;
};

DropdownList.prototype.extract = function () {
    var data = [];
    var options = [];
    var selectees = [];

    this.el.querySelectorAll("option,optgroup").forEach(option => {
        var datum = {};

        if (option.tagName == "OPTGROUP") {
            datum = {
                text: option.label,
                optgroup: true
            };
        }
        else {
            let text = option.innerText;
            if (option.dataset.display != undefined) {
                text = option.dataset.display;
            }

            datum = {
                text: text,
                value: option.value,
                extra: option.dataset.extra,
                selected: !!attr(option, "selected"),
                disabled: !!attr(option, "selected"),
                group: option.parentNode.tagName == "OPTGROUP" ? attr(option.parentNode, "label") : ""
            };
        }

        var attributes = {
            selected: !!datum.selected,
            disabled: !!datum.disabled,
            optgroup: !!datum.optgroup
        };
        var item = { data: datum, attributes };

        data.push(datum);
        options.push(item);
        if (attributes.selected) {
            selectees.push(item);
        }
    });

    this.data = data;
    this.options = options;
    this.selectees = selectees;
};

DropdownList.prototype.render = function () {
    var originalClasses = (attr(this.el, "class") || "").split(" ").filter(x => x != "dropdownlist-select") ?? [""];
    var classes = [
        "dropdownlist",
        ...originalClasses,
        this.disabled ? "dropdownlist--disabled" : "",
        this.multiple ? "dropdownlist--multiple" : "",
        this.config.showSelectedItems ? "dropdownlist--show-selectees" : ""
    ];
    var styles = [];

    if (this.config.autoWidth) {
        var maxLength = Math.round(Math.max(...[...this.data.map(x => x.text ? x.text.length : 0), this.config.placeholder.length]) * 0.7);
        styles.push(`min-width: ${maxLength}rem !important;`);
    }

    let searchTemplate = `
        <div class="dropdownlist_search">
            <input type="text" placeholder="${this.config.searchPlaceholder}..." title="${this.config.searchPlaceholder}" />
            <img src="/Content/reskin/src/assets/img/greenroom/search.svg" class="dropdownlist_search_icon" />
        </div>
    `;

    let dropdownTemplate = `
        <div class="${classes.join(" ").trim()}" style="${styles.join(' ')}" tabindex="${this.disabled ? null : 0}">
            <span class="${this.multiple ? "dropdownlist--selectees" : "dropdownlist--selectee"}">${this.config.placeholder}</span>
            <div class="dropdownlist_dropdown">
                ${this.config.searchable ? searchTemplate : ""}
                <ul></ul>
            </div>
        </div>
    `;;

    this.el.insertAdjacentHTML("afterend", dropdownTemplate);

    this.dropdown = this.el.nextElementSibling;
    this._renderSelectees();
    this._renderOptions();
};

DropdownList.prototype.update = function () {
    this.extract();
    if (this.dropdown) {
        var open = hasClass(this.dropdown, "dropdownlist--open");
        this.dropdown.parentNode.removeChild(this.dropdown);
        this.create();

        if (open) {
            trigger(this.dropdown, "click");
        }
    }

    if (attr(this.el, "disabled")) {
        this.disable();
    }
    else {
        this.enable();
    }
};

DropdownList.prototype.disable = function () {
    if (!this.disabled) {
        this.disabled = true;
        this.dropdown.clasList.add("dropdownlist--disabled");
    }
};

DropdownList.prototype.enable = function () {
    if (this.disabled) {
        this.disabled = false;
        this.dropdown.classList.remove("dropdownlist--disabled");
    }
};

DropdownList.prototype.clear = function () {
    this.reset();
    this.selectees = [];
    this._renderSelectees();
    this.update();

    this._internalChange = true;
    trigger(this.el, 'change');
};

DropdownList.prototype.destroy = function () {
    if (this.dropdown) {
        this.dropdown.parentNode.removeChild(this.dropdown);
        this.el.classList.remove("dropdownlist-select");
    }
};

DropdownList.prototype.bind = function () {
    this.dropdown.addEventListener("click", this._onClicked.bind(this));
    this.dropdown.addEventListener("keydown", this._onKeyPressed.bind(this));
    this.dropdown.addEventListener("focusin", trigger.bind(this, this.el, "focusin"));
    this.dropdown.addEventListener("focusout", trigger.bind(this, this.el, "focusout"));
    this.el.addEventListener("invalid", this._updateValidation.bind(this, this.el, 'invalid'));
    this.el.addEventListener("change", this._onSelectChanged.bind(this));
    window.addEventListener("click", this._onClickedOutside.bind(this));

    if (this.config.searchable) {
        this._bindSearch();
    }
};

DropdownList.prototype.setValue = function (value) {
    var select = this.el;
    var noSelected = true;
    var currentValue;

    if (this.multiple) {
        for (var i = 0; i < value.length; i++) {
            value[i] = String(value[i]);
        }
    }

    for (var option of select.options) {
        if (this.multiple) {
            if (value.indexOf(option.value) > -1) { //-- expect Array like [1,2,3]
                currentValue = option.value;
            }
            else {
                currentValue = null;
            }
        }
        else {
            currentValue = value;
        }

        if (option.value == currentValue && !option.disabled) {
            if (noSelected) {
                select.value = currentValue;
                noSelected = false;
            }
            option.setAttribute('selected', true);
            option.selected = true;
        } else {
            option.removeAttribute('selected');
            delete (option.selected);
        }
    }

    if (noSelected && !this.multiple && select.options.length > 0) {
        var option = select.options[0];
        option.setAttribute('selected', true);
        option.selected = true;
        select.value = option.value;
    }

    this.update();
};

DropdownList.prototype.getValue = function () {
    var select = this.el;
    if (!this.multiple) {
        return select.value;
    }

    //-- multiple
    var values = [];
    for (var option of select.options) {
        if (option.selected) {
            values.push(option.value);
        }
    }
    return values;
};

DropdownList.prototype.updateSelectValue = function () {
    if (this.multiple) {
        var select = this.el;
        this.selectees.forEach(function (selectee) {
            var el = select.querySelector(`option[value="${selectee.data.value}"]`);
            if (el) {
                el.setAttribute("selected", true);
            }
            else {
                console.error("Option not found, does it have a value?");
            }
        });
    }
    else if (this.selectees.length > 0) {
        this.el.value = this.selectees[0].data.value;
    }

    this._internalChange = true;
    trigger(this.el, 'change');
};

DropdownList.prototype.reset = function () {
    if (this.multiple) {
        var select = this.el;
        this.selectees.forEach(function (selectee) {
            var el = select.querySelector(`option[value="${selectee.data.value}"]`);
            if (el) {
                el.removeAttribute("selected");
                delete (el.selected);
            }
        });
    }
    else if (this.selectees.length > 0) {
        this.el.selectedIndex = -1;
    }

    this._internalChange = true;
    trigger(this.el, 'change');
};

DropdownList.prototype._onSelectChanged = function (e) {
    if (this._internalChange) {
        this._internalChange = false;
        return;
    }
    // console.log('_onSelectChanged', e);
    this.extract();
    this._renderSelectees();
    this._renderOptions();
};

DropdownList.prototype._renderSelectees = function () {
    if (this.multiple) {
        var html = [];
        if (this.config.showSelectees || this.selectees.length < 2) {
            this.selectees.forEach(function (selectees) {
                html.push(`<span class="dropdownlist_selectee">${selectees.data.text}</span>`);
            });
            html = html.length == 0 ? [this.config.placeholder] : html;
        }
        else if (this.selectees.length > 0) {
            html = [`${this.selectees.length} ${this.config.selectedText}`];
        }
        else {
            html = [this.config.placeholder];
        }

        this.dropdown.querySelector(".dropdownlist--selectees").innerHTML = html.join('');
    }
    else {
        var html = this.selectees.length > 0 ? this.selectees[0].data.text : this.config.placeholder;
        this.dropdown.querySelector(".dropdownlist--selectee").innerHTML = html;
    }
};

DropdownList.prototype._renderOptions = function () {
    var ul = this.dropdown.querySelector("ul");
    if (ul) {
        while (ul.firstChild) {
            ul.removeChild(ul.lastChild);
        }
        this.options.forEach(option => {
            ul.appendChild(this._renderOption(option));
        });
    }
};

DropdownList.prototype._renderOption = function (option) {
    var li = document.createElement("li");

    if (option.data.optgroup) {
        li.innerHTML = `<span>${option.data.text}</span>`;
        li.classList.add("dropdownlist_optgroup");
    }
    else {
        li.innerHTML = option.data.text;
        li.dataset.value = option.data.value;
        li.classList.add("dropdownlist_option");
        if (option.selected) {
            li.classList.add("dropdownlist_option--selected");
        }
        if (option.disabled) {
            li.classList.add("dropdownlist_option--disabled");
        }
        if (option.data.group && option.data.group.length) {
            li.classList.add("dropdownlist_option--grouped");
        }

        if (option.data.extra != undefined) {
            li.appendChild(this._renderOptionExtra(option.data.extra));
        }

        if (this.multiple) {
            var checkbox = this._renderOptionCheckbox();
            option.checkbox = checkbox;
            li.prepend(checkbox);
        }

        li.addEventListener("click", this._onOptionClicked.bind(this, option));
    }

    option.element = li;
    return li;
};

DropdownList.prototype._renderOptionExtra = function (content) {
    var span = document.createElement("span");
    span.innerHTML = content;
    span.classList.add("dropdownlist_option_extra");
    return span;
}

DropdownList.prototype._renderOptionCheckbox = function (content) {
    var span = document.createElement("span");
    span.classList.add("dropdownlist_option_checkbox");
    return span;
}

DropdownList.prototype._updateValidation = function (el, type) {
    if (type == "invalid") {
        this.dropdown.classList.add("dropdownlist--invalid");
        this.dropdown.classList.remove("dropdownlist--valid");
    }
    else {
        this.dropdown.classList.add("dropdownlist--valid");
        this.dropdown.classList.remove("dropdownlist--invalid");
    }
};

DropdownList.prototype._bindSearch = function () {
    var searchBox = this.dropdown.querySelector(".dropdownlist_search");
    if (searchBox) {
        searchBox.addEventListener("click", function (e) {
            e.stopPropagation();
            return false;
        });
    }

    searchBox.addEventListener("input", this._onSearchChanged.bind(this));
};

DropdownList.prototype._onClicked = function (event) {
    event.preventDefault();
    if (!hasClass(this.dropdown, "dropdownlist--open")) {
        this.dropdown.classList.add("dropdownlist--open");
        if (this.overflow) {
            this._openFloating();
        }
        trigger(this.el, "modalopen");
    }
    else {
        if (this.multiple) {
            if (event.target == this.dropdown.querySelector(".dropdownlist--selectees")) {
                this.dropdown.classList.remove("dropdownlist--open");
                if (this.overflow) {
                    this._closeFloating();
                }
                trigger(this.el, "modalclose");
            }
            else if (hasClass(event.target, "dropdownlist--open")) {
                this.dropdown.classList.remove("dropdownlist--open");
                if (this.overflow) {
                    this._closeFloating();
                }
                trigger(this.el, "modalclose");
            }
        }
        else {
            this.dropdown.classList.remove("dropdownlist--open");
            if (this.overflow) {
                this._closeFloating();
            }
            trigger(this.el, "modalclose");
        }
    }

    if (hasClass(this.dropdown, "dropdownlist--open")) {
        var search = this.dropdown.querySelector(".dropdownlist_search");
        if (search) {
            search.value = "";
            search.focus();
        }

        var option = this.dropdown.querySelector(".dropdownlist_option--focus");
        if (option) {
            option.classList.remove("dropdownlist_option--focus");
        }

        option = this.dropdown.querySelector(".dropdownlist_option--selected");
        if (option) {
            option.classList.add("dropdownlist_option--focus");
        }

        var options = this.dropdown.querySelectorAll("ul li");
        if (options && options.length) {
            options.forEach(function (li) {
                li.classList.remove("dropdownlist_option--hidden");
            });
        }
    } else {
        this.dropdown.focus();
    }
};

DropdownList.prototype._onOptionClicked = function (option, event) {
    var target = event.target;
    if (target.tagName != "LI") {
        target = target.parentNode;
    }

    if (!hasClass(target, "drodownlist_option--disabled")) {
        if (this.multiple) {
            if (hasClass(target, "dropdownlist_option--selected")) {
                target.classList.remove("dropdownlist_option--selected");
                this.selectees.splice(this.selectees.indexOf(option), 1);
                var element = this.el.querySelector(`option[value="${target.dataset.value}"]`);
                element.removeAttribute('selected');
                element.selected = false;
            }
            else {
                target.classList.add("dropdownlist_option--selected");
                this.selectees.push(option);
            }
        }
        else {
            this.options.forEach(function (o) {
                o.element.classList.remove("dropdownlist_option--selected");
            });
            this.selectees.forEach(function (o) {
                o.element.classList.add("dropdownlist_option--selected");
            });

            target.classList.add("dropdownlist_option--selected");
            this.selectees = [option];
        }

        this._renderSelectees();
        this.updateSelectValue();
    }
};

DropdownList.prototype._onClickedOutside = function (e) {
    if (!this.dropdown.contains(e.target)) {
        this.dropdown.classList.remove("dropdownlist--open");
        this._closeFloating();
        trigger(this.el, "modalclose");
    }
};

DropdownList.prototype._onKeyPressed = function (e) {
    // Keyboard events
    var focusedOption = this.dropdown.querySelector(".dropdownlist_option--focus");
    var open = hasClass(this.dropdown, "dropdownlist--open");

    if (e.keyCode == 13) { // Enter
        if (open) {
            trigger(focusedOption, "click");
        }
        else {
            trigger(this.dropdown, "click");
        }
    }
    else if (e.keyCode == 40) { // Down
        if (!open) {
            trigger(this.dropdown, "click");
        }
        else {
            var next = this._findNext(focusedOption);
            if (next) {
                var option = this.dropdown.querySelector(".dropdownlist_option--focus");
                option.classList.remove("dropdownlist_option--focus");
                next.classList.add("dropdownlist_option--focus");
            }
        }
        e.preventDefault();
    }
    else if (e.keyCode == 38) { // Up
        if (!open) {
            trigger(this.dropdown, "click");
        }
        else {
            var prev = this._findPrev(focusedOption);
            if (prev) {
                var option = this.dropdown.querySelector(".dropdownlist_option--focus");
                option.classList.remove("dropdownlist_option--focus");
                prev.classList.add("dropdownlist_option--focus");
            }
        }
        e.preventDefault();
    }
    else if (e.keyCode == 27 && open) { // Esc
        trigger(this.dropdown, "click");
    }

    return false;
};

DropdownList.prototype._findNext = function (el) {
    if (el) {
        el = el.nextElementSibling;
    }
    else {
        el = this.dropdown.querySelector(".dropdownlist_option");
    }

    while (el) {
        if (!hasClass(el, "dropdownlist_option--disabled") && !hasClass(el, "dropdownlist_option--hidden")) {
            return el;
        }
        el = el.nextElementSibling;
    }

    return null;
};

DropdownList.prototype._findPrev = function (el) {
    if (el) {
        el = el.previousElementSibling;
    }
    else {
        el = this.dropdown.querySelector(".dropdownlist_option:last-child");
    }

    while (el) {
        if (!hasClass(el, "dropdownlist_option--disabled") && !hasClass(el, "dropdownlist_option--hidden")) {
            return el;
        }
        el = el.previousElementSibling;
    }

    return null;
};

DropdownList.prototype._onSearchChanged = function (event) {
    var open = hasClass(this.dropdown, "dropdownlist--open");
    var text = event.target.value;
    text = text.toLowerCase();

    if (text == "") {
        this.options.forEach(function (option) {
            option.classList.remove("dropdownlist_option--hidden");
        });
    }
    else if (open) {
        var matchReg = new RegExp(text);
        this.options.forEach(function (option) {
            var optionText = option.data.text.toLowerCase();
            var matched = matchReg.test(optionText);
            if (matched) {
                option.classList.remove("dropdownlist_option--hidden");
            }
            else {
                option.classList.add("dropdownlist_option--hidden");
            }
        });
    }

    this.dropdown.querySelectorAll(".dropdownlist_option--focus").forEach(function (option) {
        option.classList.remove("dropdownlist_option--focus");
    });

    var firstOption = this._findNext(null);
    firstOption.classList.add("dropdownlist_option--focus");
};

DropdownList.prototype._getScrollParents = function () {
    var parents = [];
    var parent = this.el.parentElement;
    while (parent) {
        var style = getComputedStyle(parent);
        if (/(auto|scroll)/.test(style.overflowX || style.overflowY || style.overflow)) {
            parents.push(parent);
        }
        parent = parent.parentElement;
    }
    parents.push(window); // toujours écouter le scroll window
    return parents;
};

DropdownList.prototype._positionDropdown = function () {
    var rect = this.dropdown.getBoundingClientRect();
    var dd = this._floatingDropdown;

    dd.style.display = 'flex';
    dd.style.position = 'fixed';
    dd.style.left = rect.left + 'px';
    dd.style.top = rect.bottom + 'px';
    dd.style.minWidth = (rect.width * 1.2) + 'px'; // 120% of rect width.
    dd.style.zIndex = '9999';
    dd.style.setProperty('--trigger-width', (rect.width - 2) + 'px'); // -2 to exclude border width.
};

DropdownList.prototype._openFloating = function () {
    var dd = this.dropdown.querySelector('.dropdownlist_dropdown');

    this._floatingDropdown = dd;
    this._originalParent = dd.parentNode;
    document.body.appendChild(dd);

    this._positionDropdown();

    // Écouter les scrolls pour repositionner
    this.parents = this._getScrollParents();
    this._boundReposition = this._onScrollReposition.bind(this);
    this.parents.forEach(function (p) {
        p.addEventListener('scroll', this._boundReposition, { passive: true });
    }.bind(this));
};

DropdownList.prototype._closeFloating = function () {
    if (this._floatingDropdown && this._originalParent) {
        // Remettre le dropdown à sa place originale
        this._originalParent.appendChild(this._floatingDropdown);
        this._floatingDropdown.style.display = 'none';
        this._floatingDropdown.style.position = '';
        this._floatingDropdown.style.left = '';
        this._floatingDropdown.style.top = '';
        this._floatingDropdown.style.minWidth = '';
        this._floatingDropdown.style.zIndex = '';
    }
    
    // Cleanup listeners
    if (this.parents) {
        this.parents.forEach(function (p) {
            p.removeEventListener('scroll', this._boundReposition);
        }.bind(this));
    }
};

DropdownList.prototype._onScrollReposition = function () {
    if (!this._rafPending) {
        this._rafPending = true;
        requestAnimationFrame(function () {
            this._positionDropdown();
            this._rafPending = false;
        }.bind(this));
    }
};